import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { TweenMax } from 'gsap';
import { AutenticationService } from '../../../autentication/autentication.service';
import { Auth } from '../../../autentication/Auth';
import { EventTypes } from '../../../shared/EventTypes';
import { ThemeService } from '../../../shared/theme-service/theme.service';
import { AppEventDispatcher } from '../../../utils/AppEventDispatcher';
import { ChangePassEventType } from '../change-pass.event.type';
import { ChangePassService } from '../change-pass.service';
import { ChangePass } from '../model/change-pass';

@Component({
	selector: 'app-reset-with-steps',
	templateUrl: './reset-with-steps.component.html',
	styleUrls: ['./reset-with-steps.component.styl'],
	encapsulation: ViewEncapsulation.None
})
export class ResetWithStepsComponent implements OnInit, OnDestroy {

	@Input() submited: boolean;
	@Input() changePassData: ChangePass;
	@Output() onSetForm: EventEmitter<FormGroup> = new EventEmitter<FormGroup>();

	public step = 1;
	public title = '';
	public firstStep = 1;
	public lastStep = 5;
	public labelNextButton = 'Próximo';
	public active = false;
	public isTransactionUser = false;
	public isAttendant = false;
	public formPass: FormGroup;
	public inputToKeypadSendValue: any;
	public tokenMessage: string;

	constructor
		(
			private formBuilder: FormBuilder,
			private autenticationService: AutenticationService,
			private changePassService: ChangePassService,
			private themeService: ThemeService,
			private authService: AutenticationService
		) {
		this.formPass = formBuilder.group({
			oldPassword: ['', [Validators.required]],
			eletronicSignature: ['', [Validators.required]],
			passwords: this.formBuilder.group({
				password: ['', [Validators.required]],
				repeatPassword: ['', [Validators.required]],
			}, { validator: this.passwordConfirming }),
		});
	}

	ngOnInit() {
		this.setIsTransactionUser();
		this.setIsAttendant();
		this.setTitleAndLabelButton();
		this.setInputTarget();
		this.setFormControls();
		this.updateLastStep();
		this.sendSmsToken();
		this.AddListeners();
	}

	ngOnDestroy() {
		this.removeListeners();
	}

	public prev() {
		this.step--;
		this.setTitleAndLabelButton();
		this.setInputTarget();
	}

	public next() {
		if (this.step === this.lastStep) {
			this.sendForm();
		} else {
			this.step++;
			this.setTitleAndLabelButton();
			this.setInputTarget();
		}
	}

	public setInputTarget() {
		switch (this.step) {
			case 1:
				this.inputToKeypadSendValue = this.formPass.get('oldPassword');
				break;
			case 2:
				this.inputToKeypadSendValue = this.formPass.get(['passwords', 'password']);
				break;
			case 3:
				this.inputToKeypadSendValue = this.formPass.get(['passwords', 'repeatPassword']);
				break;
			case 4:
				this.inputToKeypadSendValue = this.formPass.get('eletronicSignature');
				break;
			case 5:
				this.inputToKeypadSendValue = this.formPass.get('token');
				break;
		}
	}

	public setInputValueFromKeyPad(val: Array<number>) {
		if (this.inputToKeypadSendValue.value.length < 6) {
			this.inputToKeypadSendValue.setValue(this.inputToKeypadSendValue.value.toString() + val[0].toString());
		}
	}

	public onCorrect() {
		this.inputToKeypadSendValue.setValue('');
	}

	public sendSmsToken() {
		if (Auth.getInstance().getUser().tokenSms) {

			AppEventDispatcher.dispatch(EventTypes.PRELOAD, { show: true });

			this.authService.requestTokenSMS().subscribe((data: any) => {
				this.tokenMessage = data.Mensagem;
				AppEventDispatcher.dispatch(EventTypes.PRELOAD, { show: false });
			}, () => {
				AppEventDispatcher.dispatch(EventTypes.MESSAGE.SNACK,
					{ msg: 'Erro ao solicitar token por SMS, por favor, clique no botão reenviar token.' });
			});
		}
	}

	public passwordConfirming(c: AbstractControl): { invalid: boolean } {
		if (c.get('password').value !== c.get('repeatPassword').value) {
			return { invalid: true };
		}
	}

	private setIsTransactionUser() {
		this.isTransactionUser = Auth.getInstance().getUser().rule === 'TRANSACIONAR';
	}

	private setIsAttendant() {
		this.isAttendant = this.themeService.isAttendant();
	}

	private updateLastStep() {
		if (!this.isTransactionUser) {
			this.lastStep = 4;
		}
	}

	private setFormControls() {
		if (this.isTransactionUser) {
			this.formPass.addControl('token', new FormControl('', Validators.required));
		} else {
			if (this.formPass.controls.token) {
				this.formPass.removeControl('token');
			}
		}
	}

	private AddListeners() {
		AppEventDispatcher.listen(ChangePassEventType.STEPS.CHANGE_PASS_RESET, () => {
			this.active = true;
		});

		AppEventDispatcher.listen(ChangePassEventType.SEND.CHANGE_PASS_SEND, () => {
			this.sendForm();
		});
	}

	private removeListeners() {
		AppEventDispatcher.removeListener(ChangePassEventType.SEND.CHANGE_PASS_SEND);
		AppEventDispatcher.removeListener(ChangePassEventType.STEPS.CHANGE_PASS_RESET);
	}

	private setTitleAndLabelButton() {
		switch (this.step) {
			case 1:
				this.title = 'Passo 1: Digite sua senha atual';
				this.labelNextButton = 'Próximo';
				break;
			case 2:
				this.title = 'Passo 2: Defina uma nova senha';
				this.labelNextButton = 'Próximo';
				break;
			case 3:
				this.title = 'Passo 3: Confirmar nova senha';
				this.labelNextButton = 'Próximo';
				break;
			case 4:
				this.title = 'Digite sua assinatura eletrônica';
				this.labelNextButton = (this.isTransactionUser ? 'Próximo' : 'Finalizar');
				break;
			case 5:
				this.title = 'Digite o token';
				this.labelNextButton = 'Finalizar';
				break;
		}
	}

	addAuthToken() {
		Auth.passToken = this.formPass.value.token ? this.formPass.value.token : '';
		Auth.assinaturaEletronica = this.formPass.value.eletronicSignature ? this.formPass.value.eletronicSignature : '';
	}

	private sendForm() {

		this.submited = true;

		if (this.formPass.valid) {

			this.preload(true);
			this.submited = false;

			let time = 1;
			const timeLoader = setInterval(() => {
				time++;
			}, 1000);

			this.addAuthToken();

			this.autenticationService.sendDigitalSignature(this.formPass.value.eletronicSignature).subscribe(d => {
				if (d.Sucesso) {

					this.preload(true);

					let secondTime = 1;
					const secondTimeLoader = setInterval(() => {
						secondTime++;
					}, 1000);

					this.changePassService.changePassword(this.formPass.value).subscribe(data => {

						if (data.Sucesso) {
							this.changePassData.success = true;
						} else {
							this.changePassData.success = false;
						}

						this.changePassData.message = data.Mensagem;
						this.checkAfterSend(timeLoader, time);

						this.hide();

					}, error => {
						this.checkAfterSend(timeLoader, time);

						if (error.error.Tipo !== 'tokenDatablinkValidado') {
							this.hide();
							this.changePassData.success = false;
							this.changePassData.message = error.error.Mensagem;
						} else {
							AppEventDispatcher.dispatch(EventTypes.MESSAGE.SNACK, { msg: error.error.Mensagem });
							AppEventDispatcher.dispatch(ChangePassEventType.ERROR, { step: ChangePassEventType.SEND.CHANGE_PASS_SEND });
						}

					});
				}
			}, error => {
				this.preload(false);
				AppEventDispatcher.dispatch(EventTypes.MESSAGE.SNACK, { msg: error.error.Mensagem });
				AppEventDispatcher.dispatch(ChangePassEventType.ERROR, { step: ChangePassEventType.SEND.CHANGE_PASS_SEND });
			});
		}
	}

	private preload(active: boolean, delay = 0) {
		AppEventDispatcher.dispatch(EventTypes.PRELOAD,
			{ element: '[data-component="reset-with-steps"] .content', show: active, delay: delay });
	}

	private checkAfterSend(timeLoader, time) {
		if (time > 3) {
			this.preload(false);
		} else {
			TweenMax.delayedCall(3 - time, () => {
				this.preload(false);
			});
		}
		clearInterval(timeLoader);
	}

	private hide() {
		TweenMax.delayedCall(1, () => {
			AppEventDispatcher.dispatch(ChangePassEventType.STEPS.CHANGE_PASS_WARNING, { show: true });
			this.active = false;
		});
	}
}



// WEBPACK FOOTER //
// /home/ubuntu/workdir/workspace/Front End Angular (NGINX)/omni-internetbanking/dev-omni-internetbanking-deploy/src/app/master/change-pass/reset-with-steps/reset-with-steps.component.ts